// Pure DOM renderer. Called on every state change. No logic, no side effects.

const $ = id => document.getElementById(id)

export function render(state) {
  const { status, report, sections, sectionIndex, historyIndex, timerRemaining, timerTotal,
          pendingTranscript, errorMessage } = state

  // Error banner
  const errBanner = $('error-banner')
  if (errorMessage) {
    $('error-text').textContent = errorMessage
    errBanner.hidden = false
  } else {
    errBanner.hidden = true
  }

  if (status === 'IDLE' || !report) return

  if (status === 'DONE') { _renderDone(state); return }

  const inHistory    = status === 'HISTORY_MODE' && historyIndex !== null
  const isRecording  = status === 'RECORDING'
  const isTranscrib  = status === 'TRANSCRIBING'
  const isConfirming = status === 'CONFIRMING'

  const displayIdx = inHistory ? historyIndex : sectionIndex
  const section    = sections[displayIdx]
  if (!section) return

  const total = sections.length

  // ── header ──
  $('project-name').textContent = report.project
  $('section-progress').textContent = inHistory
    ? `history ${displayIdx + 1} of ${total}`
    : `${sectionIndex + 1} of ${total}`

  // ── history bar ──
  const histBar = $('history-bar')
  histBar.hidden = !inHistory
  if (inHistory) $('history-pos').textContent = `${displayIdx + 1} / ${sectionIndex}`

  // ── recording indicator ──
  $('recording-indicator').hidden = !isRecording

  // ── kind label ──
  if (isRecording)  $('kind-label').textContent = 'recording'
  else if (isTranscrib) $('kind-label').textContent = 'transcribing…'
  else if (isConfirming) $('kind-label').textContent = 'your response'
  else $('kind-label').textContent = _kindLabel(section, inHistory)

  // ── title ──
  if (isTranscrib)   $('section-title').textContent = 'Transcribing…'
  else if (isConfirming) $('section-title').textContent = 'Did you say…'
  else if (isRecording) {
    const s = sections[sectionIndex]
    $('section-title').textContent = s?.title ?? s?.prompt ?? 'Recording'
  } else {
    $('section-title').textContent = section.title ?? section.prompt ?? ''
  }

  // ── body ──
  if (isConfirming) {
    _showContent(pendingTranscript ?? '…')
    $('section-image').hidden = true
    $('image-caption').hidden = true
    $('options-list').hidden = true
    $('open-hint').hidden = true
  } else if (isRecording || isTranscrib) {
    // show the section being recorded for (dimmed context)
    const s = sections[sectionIndex]
    _hideAllBody()
    if (s?.type === 'text') _showContent(s.content)
    else if (s?.type === 'question') {
      $('open-hint').textContent = s.prompt
      $('open-hint').hidden = false
    }
  } else {
    _renderBody(section, state)
  }

  // ── timer bar ──
  const timerWrap = $('timer-wrap')
  const showTimer = (status === 'COUNTING_DOWN' || status === 'CONFIRMING') && timerTotal > 0
  if (showTimer) {
    timerWrap.hidden = false
    const pct = (timerRemaining / timerTotal) * 100
    const fill = $('timer-fill')
    fill.style.transition = timerRemaining < timerTotal ? 'width 0.9s linear' : 'none'
    fill.style.width = `${pct}%`
    $('timer-seconds').textContent = `${timerRemaining}s`
  } else {
    timerWrap.hidden = true
  }

  // ── hint ──
  $('hint-text').textContent = _hintText(status, inHistory, section)
}

function _renderBody(section, state) {
  const contentEl = $('section-content')
  const imageEl   = $('section-image')
  const captionEl = $('image-caption')
  const optionsEl = $('options-list')
  const openEl    = $('open-hint')

  contentEl.hidden = true
  imageEl.hidden   = true
  captionEl.hidden = true
  optionsEl.hidden = true
  openEl.hidden    = true

  if (section.type === 'text') {
    contentEl.textContent = section.content
    contentEl.hidden = false
  } else if (section.type === 'media') {
    imageEl.src = section.file
    imageEl.alt = section.title ?? ''
    imageEl.hidden = false
    if (section.caption) {
      captionEl.textContent = section.caption
      captionEl.hidden = false
    }
  } else if (section.type === 'question') {
    if (section.kind === 'multiple_choice') {
      _renderOptions(section, state.responses[section.id]?.selected ?? null)
      optionsEl.hidden = false
    } else {
      openEl.textContent = state.status === 'WAITING_ANSWER'
        ? 'Tap to record your answer, or double-tap to skip.'
        : section.prompt
      openEl.hidden = false
    }
  }
}

function _hideAllBody() {
  $('section-content').hidden = true
  $('section-image').hidden   = true
  $('image-caption').hidden   = true
  $('options-list').hidden    = true
  $('open-hint').hidden       = true
}

function _showContent(text) {
  $('section-content').textContent = text
  $('section-content').hidden = false
  $('section-image').hidden   = true
  $('image-caption').hidden   = true
  $('options-list').hidden    = true
  $('open-hint').hidden       = true
}

function _renderOptions(section, selectedId) {
  const el = $('options-list')
  el.innerHTML = ''
  for (const opt of section.options ?? []) {
    const btn = document.createElement('button')
    btn.className = 'option-btn' + (selectedId === opt.id ? ' selected' : '')
    btn.dataset.optionId = opt.id
    btn.innerHTML =
      `<span class="option-key">${opt.id.toUpperCase()}</span>` +
      `<span>${opt.text}</span>`
    el.appendChild(btn)
  }
}

function _renderDone(state) {
  $('project-name').textContent = state.report.project
  $('section-progress').textContent = 'done'
  $('history-bar').hidden      = true
  $('recording-indicator').hidden = true
  $('kind-label').textContent  = ''
  $('section-title').textContent = 'Session complete'
  $('section-content').textContent =
    `Reviewed ${state.sections.length} section${state.sections.length !== 1 ? 's' : ''}.`
  $('section-content').hidden = false
  $('section-image').hidden   = true
  $('image-caption').hidden   = true
  $('options-list').hidden    = true
  $('open-hint').hidden       = true
  $('timer-wrap').hidden      = true
  $('hint-text').textContent  = 'Triple-tap to review history.'
}

function _kindLabel(section, inHistory) {
  if (inHistory) return 'history'
  switch (section.type) {
    case 'text':     return 'section'
    case 'media':    return 'image'
    case 'question': return section.required !== false ? 'question — required' : 'question — optional'
    default:         return ''
  }
}

function _hintText(status, inHistory, section) {
  if (inHistory)              return '← triple-tap back  ·  →→ double-tap forward  ·  tap to return'
  switch (status) {
    case 'SPEAKING':           return 'tap to pause  ·  ←←← triple-tap for history'
    case 'PAUSED':             return 'tap to resume'
    case 'COUNTING_DOWN':      return 'tap to comment  ·  →→ double-tap to skip  ·  ←←← history'
    case 'WAITING_ANSWER':     return section?.kind === 'multiple_choice'
                                 ? 'tap an option  ·  or tap once to answer by voice'
                                 : 'tap to record answer  ·  →→ double-tap to skip'
    case 'RECORDING':          return 'tap to stop  ·  →→ double-tap to cancel'
    case 'TRANSCRIBING':       return ''
    case 'CONFIRMING':         return 'tap to re-record  ·  wait to confirm'
    case 'DONE':               return 'triple-tap to review history'
    default:                   return ''
  }
}
